import { useTranslation } from 'next-i18next/pages'
import { Grid, Box, Heading, Icon, SimpleGrid, Text } from '@chakra-ui/react'
import { VerifyFormButton } from '../atoms/VerifyFormButton'
import { VerifyFormTextarea } from '../atoms/VerifyFormTextarea'
import { EmptyPlaceholder } from '../molecules/EmptyPlaceholder'
import { VerifyFormTitle } from '../molecules/VerifyFormTitle'
import { CoinflipChoice } from '@/__generated__/graphql'
import CoinHeadIcon from '@/public/assets/images/coinflip/coin-head.svg'
import CoinTailIcon from '@/public/assets/images/coinflip/coin-tail.svg'
import { FormProvider, useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { convertUInt32ToFloats, getUint32 } from '../pkg/rng_v2'
import { sha256 } from '@noble/hashes/sha256'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Section } from '../utils'

const schema = yup
  .object({
    signature: yup
      .string()
      .required()
      .label('Signature')
      .test('is-hex', '${path} is not hex', (value) => /^(0x)?[0-9a-fA-F]+$/.test(value || '')),
  })
  .required()

export const CoinflipVerify = () => {
  const { t } = useTranslation()
  const form = useForm({
    resolver: yupResolver(schema),
  })

  const [state, setState] = useState<{ result: CoinflipChoice } | undefined>()

  useEffect(() => {
    const { unsubscribe } = form.watch(() => {
      setState(undefined)
    })
    return () => unsubscribe()
  }, [form.watch])

  const handleClick = async () => {
    await form.trigger()
    if (!form.formState.isValid) {
      setState(undefined)
      return
    }
    const signature = form.getValues('signature').replace('0x', '') as any

    const randomness = Buffer.from((await sha256(signature)) as any).toString('hex')

    const result = Math.floor(
      convertUInt32ToFloats(
        getUint32({
          serverKey: Buffer.from(randomness, 'hex'),
          userSeed: Buffer.from('coinflip'),
          count: 1,
        })[0]
      ) * 2
    )

    setState({
      result: result === 0 ? CoinflipChoice.Heads : CoinflipChoice.Tails,
    })
  }

  return (
    <Grid gap={5} gridTemplateColumns={{ base: '1fr', lg: '260px auto' }}>
      <Box p={5} rounded={'24px'} bg={'ninja.600'}>
        <FormProvider {...form}>
          <VerifyFormTitle mode="drng" />
          <VerifyFormTextarea
            label={t('verify.signature')}
            placeholder={t('verify.enter-signature')}
            name="signature"
          />
          <VerifyFormButton onClick={handleClick} />
        </FormProvider>
      </Box>
      <Box p={5} minH={{ base: '0', lg: '375px' }} rounded={'24px'} bg={'ninja.600'}>
        <Heading fontSize={'xl'} mb={{ base: 2, lg: 0 }}>
          {t('common.result')}
        </Heading>
        {!state ? <EmptyPlaceholder game={Section.COINFLIP} /> : <CoinflipVerifyResult result={state.result} />}
      </Box>
    </Grid>
  )
}

const CoinflipVerifyResult = ({ result }: { result: CoinflipChoice }) => {
  const { t } = useTranslation()
  const isHead = result === CoinflipChoice.Heads

  const winShadow = '0px -4px 43.7px 0px #68F27F inset'

  return (
    <SimpleGrid
      h={{ base: 'auto', lg: 'calc(100% - 24px)' }}
      alignContent={'center'}
      maxW={'500px'}
      mx={'auto'}
      columns={2}
      gap={3}
    >
      <Box
        py={10}
        px={0}
        bg={'ninja.700'}
        rounded={'2xl'}
        boxShadow={isHead ? winShadow : 'none'}
        opacity={!isHead ? 0.6 : 'none'}
      >
        <Icon mb={2} display={'block'} mx={'auto'} w={20} h={20} as={CoinHeadIcon} alt="" />
        <Text fontSize={'lg'} fontWeight={700} textAlign={'center'}>
          {t('coinflip.heads')}
        </Text>
      </Box>
      <Box
        py={10}
        px={0}
        bg={'ninja.700'}
        rounded={'2xl'}
        boxShadow={!isHead ? winShadow : 'none'}
        opacity={isHead ? 0.6 : 'none'}
      >
        <Icon mb={2} display={'block'} mx={'auto'} w={20} h={20} as={CoinTailIcon} alt="" />
        <Text fontSize={'lg'} fontWeight={700} textAlign={'center'}>
          {t('coinflip.tails')}
        </Text>
      </Box>
    </SimpleGrid>
  )
}
